import React, { useState, useEffect } from 'react';

const DEFAULT_SUBSTANCE_OPTIONS = [
    { value: 'coffee', label: 'Coffee' },
    { value: 'coffee, latte', label: 'Latte' },
    { value: 'decaf_coffee', label: 'Decaf Coffee' },
    { value: 'tea', label: 'Tea' },
    { value: 'yerba_mate', label: 'Yerba mate' },
    { value: 'kombucha', label: 'Kombucha' },
    { value: 'cannabis', label: 'Cannabis' },
    { value: 'edible cannabis', label: 'Edible Cannabis' },
];

const SUBSTANCE_BEHAVIOUR = {
    'coffee, latte': { defaultUnits: 'fl oz' },
    'cannabis': { daily: true, fixed: { quantity: 1, units: 'unknown' } },
    'edible cannabis': { daily: true, defaultUnits: 'mg' },
};

function behaviourFor(substance) {
    return SUBSTANCE_BEHAVIOUR[substance] || {};
}

export default function SubstancesForm() {
    const [substance, setSubstance] = useState('');
    const [quantity, setQuantity] = useState('');
    const [units, setUnits] = useState('g');
    const [timestamp, setTimestamp] = useState('');
    const [date, setDate] = useState('');
    const [customSubstance, setCustomSubstance] = useState('');
    const [fetchedTypes, setFetchedTypes] = useState([]);
    const [error, setError] = useState('');

    useEffect(() => {
        fetch('https://life.pmusgrave.dev/substances/types')
            .then((res) => res.json())
            .then((types) => setFetchedTypes(Array.isArray(types) ? types : []))
            .catch((err) => {
                console.log(err.message);
            });
    }, []);

    const knownValues = new Set(DEFAULT_SUBSTANCE_OPTIONS.map((o) => o.value));
    const extraOptions = fetchedTypes
        .filter((type) => type && !knownValues.has(type))
        .sort()
        .map((type) => ({ value: type, label: type }));
    const substanceOptions = [...DEFAULT_SUBSTANCE_OPTIONS, ...extraOptions];

    const behaviour = behaviourFor(substance);

    function handleSubstanceChange(e) {
        const next = behaviourFor(e.target.value);
        setSubstance(e.target.value);
        setUnits(next.fixed ? next.fixed.units : (next.defaultUnits || 'g'));
        setError('');
    }

    function handleCustomSubstanceChange(e) {
        setCustomSubstance(e.target.value);
    }

    function handleQuantityChange(e) {
        setQuantity(e.target.value);
    }

    function handleUnitsChange(e) {
        setUnits(e.target.value);
    }

    function handleTimestampChange(e) {
        setTimestamp(e.target.value);
    }

    function handleDateChange(e) {
        setDate(e.target.value);
    }

    function resetForm() {
        setSubstance('');
        setQuantity('');
        setUnits('g');
        setCustomSubstance('');
        setTimestamp('');
        setDate('');
    }

    async function handleSubmit() {
        setError('');
        const submittedType = substance === 'custom' ? customSubstance : substance;
        if (!submittedType) {
            setError('Pick a substance.');
            return;
        }
        if (!behaviour.fixed && !(quantity !== '' && Number.isFinite(parseFloat(quantity)))) {
            setError('Quantity is required.');
            return;
        }
        if (behaviour.daily && !date) {
            setError('Pick a date.');
            return;
        }

        const body = {
            type: submittedType,
            quantity: behaviour.fixed ? behaviour.fixed.quantity : parseFloat(quantity),
            units: behaviour.fixed ? behaviour.fixed.units : units,
        };
        if (behaviour.daily) {
            body.binary = true;
            body.date = date;
        } else {
            body.timestamp = timestamp ? new Date(parseInt(timestamp)).getTime() : Date.now();
        }

        const req = {
            method: 'POST',
            body: JSON.stringify(body),
            headers: {
                'Content-type': 'application/json; charset=UTF-8',
            },
        };

        try {
            const res = await fetch('https://life.pmusgrave.dev/substances', req);
            if (!res || res.status < 200 || res.status >= 300) {
                setError(`Submission failed (${res && res.status}). Nothing was recorded.`);
                return;
            }
        } catch (err) {
            setError(`Submission failed: ${err.message}. Nothing was recorded.`);
            return;
        }

        if (submittedType && !knownValues.has(submittedType) && !fetchedTypes.includes(submittedType)) {
            setFetchedTypes((prev) => [...prev, submittedType]);
        }
        resetForm();
    }

    return (
        <div className="form-container">
            <h2>Substances Form</h2>
            <form onSubmit={e => e.preventDefault()}>
                <div className="form-group">
                    <label htmlFor="substance">Substance:</label>
                    <select id="substance" value={substance} onChange={handleSubstanceChange}>
                        <option value="">Select a substance</option>
                        {substanceOptions.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                        <option value="custom">Custom</option>
                    </select>
                </div>
                {substance === 'custom' && (
                    <div className="form-group">
                        <label htmlFor="customSubstance">Custom Substance:</label>
                        <input
                            id="customSubstance"
                            type="text"
                            placeholder="Enter custom substance"
                            value={customSubstance}
                            onChange={handleCustomSubstanceChange}
                            required
                        />
                    </div>
                )}
                {!behaviour.fixed && (
                    <div className="form-group">
                        <label htmlFor="quantity">Quantity:</label>
                        <input
                            id="quantity"
                            type="number"
                            step="0.1"
                            value={quantity}
                            onChange={handleQuantityChange}
                            required
                        />
                    </div>
                )}
                {!behaviour.fixed && (
                    <div className="form-group">
                        <label htmlFor="units">Units:</label>
                        <select id="units" value={units} onChange={handleUnitsChange}>
                            <option value="g">g</option>
                            <option value="fl oz">fl oz</option>
                            <option value="mg">mg</option>
                            <option value="unknown">unknown</option>
                        </select>
                    </div>
                )}
                {behaviour.daily ? (
                    <div className="form-group">
                        <label htmlFor="date">Date:</label>
                        <input
                            id="date"
                            type="date"
                            value={date}
                            onChange={handleDateChange}
                            required
                        />
                    </div>
                ) : (
                    <div className="form-group">
                        <label htmlFor="timestamp">Timestamp (optional):</label>
                        <input
                            id="timestamp"
                            type="number"
                            value={timestamp}
                            onChange={handleTimestampChange}
                            placeholder="Unix timestamp"
                        />
                    </div>
                )}
                {error && <div className="form-error" role="alert">{error}</div>}
                <button onClick={handleSubmit} className="submit-button">
                    Submit
                </button>
            </form>
        </div>
    );
}
